import { ROLE } from '../../auth'
import { MatchingFile } from '../../matching/core/_models'

/**
 * Who works the concierge window; the merchant roles only for their merchants. The admin comes
 * through the hierarchy. Same roles as the backend's ConciergeController::MODULE_ROLES.
 */
export const CONCIERGE_ROLES: ROLE[] = [ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER]

/** Concierge deposit as the staff window sees it (`GET /api/internal/payment/{token}/concierge`). */
export interface ConciergeIssuance {
  method: string | null
  adapter: string
  amount: number | null
  requisite: string | null
  bank: string | null
  holder: string | null
  paymentUrl: string | null
  externalTransactionId: number
  status: string
}

export interface ConciergeHistoryItem {
  action:
    | 'METHOD_SET'
    | 'AMOUNT_SET'
    | 'SEARCH_STARTED'
    | 'ISSUED'
    | 'ISSUANCE_CANCELLED'
    | 'DEADLINE_EXTENDED'
    | 'LATE_CREDITED'
    | 'EXPIRED_BY_STAFF'
    | 'LATE_PAYMENT_SEEN'
    | 'CLIENT_RETURNED'
  at: string
  user: string | null
  externalTransactionId: number | null
  payload: Record<string, unknown>
}

/**
 * A payment that reached a requisite staff no longer hold, as it can be credited now:
 * AMOUNT — staff name the amount, CONFIRMED — the adapter reported it, AWAITING_CONFIRMATION — a
 * provider has not confirmed the payment yet.
 */
export interface ConciergeLateCredit {
  externalTransactionId: number
  adapter: string
  mode: 'AMOUNT' | 'CONFIRMED' | 'AWAITING_CONFIRMATION'
  amount: number | null
}

export interface ConciergeSummary {
  token: string
  merchantTransactionId: string | null
  /** The merchant's id of the client and the token of its card. */
  client: { externalId: string; token: string } | null
  status: string
  paymentMethod: string | null
  requestedAmount: number | null
  currency: string
  deadlineAt: string
  maxExtendMinutes: number
  excludedAdapterIds: number[]
  allowedMethods: string[]
  issuance: ConciergeIssuance | null
  lateCredit: ConciergeLateCredit | null
  /** The last search ended with nothing shown to the client: which adapter answered last and how. */
  searchFailure: { adapter: string; error: string | null } | null
  variants: Array<{ id: number; amount: number }>
  canCancelIssuance: boolean
  disputeOpen: boolean
  /** The deposit's receipts, in the matching deposit list shape. */
  files: MatchingFile[]
  /** Opens the client's dialog in the Verbox operator app; null while Verbox does not know the client. */
  chatUrl: string | null
  history: ConciergeHistoryItem[]
}

export interface ConciergeRoutingAdapter {
  id: number | null
  name: string
  priority: number
  enabled: boolean
  disabledReason: string | null
}

export interface ConciergeRoutingChain {
  method: string
  currency: string
  adapters: ConciergeRoutingAdapter[]
}

/** Stage of the window, derived from the ticket status. */
export type ConciergeStage = 'manual' | 'searching' | 'variants' | 'issued' | 'receipt' | 'final' | 'waiting-client'
